/**
 * Global toast container.
 *
 * Toasts are transient bottom-right notifications that don't steal
 * focus. Mounted once at the app shell so any component can fire one
 * via `toast({...})`. We keep it deliberately small — no toast library
 * dependency. Auto-dismiss is the default; rich toasts (with action
 * buttons) stay until acted on.
 *
 * Design notes:
 *   - Stacks vertically, newest at the bottom near the user's thumb.
 *   - Honours `prefers-reduced-motion` via index.css.
 *   - Doesn't block clicks elsewhere (pointer-events:none on the
 *     container, restored on each toast card).
 *   - Has a soft persistence layer for proposal toasts: a task can
 *     emit a toast that survives an in-app navigation by re-emitting
 *     when the user returns to the tab. We don't persist across
 *     page reloads — the user can find the proposal on /calendar.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { IconClose } from './Icon';

export interface ToastAction {
  label: string;
  /** Run on click. Toast dismisses unless `keepOpen: true` is returned. */
  onClick: () => void | { keepOpen?: boolean } | Promise<void | { keepOpen?: boolean }>;
  /** 'primary' is the headline action (filled bg); 'ghost' is a quiet
   *  secondary. Default 'ghost'. */
  variant?: 'primary' | 'ghost';
}

export interface ToastOptions {
  /** Stable id; lets the same task replace its existing toast instead
   *  of stacking duplicates. */
  id?: string;
  title: ReactNode;
  body?: ReactNode;
  actions?: ToastAction[];
  /** 0 = sticky (until dismissed). Default 6000ms for plain, sticky
   *  for any toast that has actions. */
  durationMs?: number;
  /** Tint of the leading accent bar. */
  tone?: 'neutral' | 'accent' | 'success' | 'warning';
}

interface ToastRow extends ToastOptions {
  id: string;
  pinned: boolean;
}

interface ToastCtx {
  toast: (opts: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function useToast(): ToastCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRow[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (opts: ToastOptions): string => {
      const id = opts.id ?? `t-${Math.random().toString(36).slice(2, 9)}`;
      // Auto-dismiss only if no actions and durationMs isn't 0.
      const sticky = !!(opts.actions && opts.actions.length > 0);
      const duration = sticky ? 0 : opts.durationMs ?? 6000;

      setItems((cur) => {
        const filtered = cur.filter((t) => t.id !== id);
        return [...filtered, { ...opts, id, pinned: sticky }];
      });

      // Clear any previous timer for this id so replacing a toast
      // resets its countdown.
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Expose a window-level fallback for code paths that can't easily
  // reach the React tree (e.g. fetch error handlers in api.ts).
  useEffect(() => {
    (window as unknown as { __synfolio_toast?: typeof toast }).__synfolio_toast = toast;
    return () => {
      (window as unknown as { __synfolio_toast?: typeof toast }).__synfolio_toast = undefined;
    };
  }, [toast]);

  useEffect(() => {
    return () => {
      for (const t of timers.current.values()) clearTimeout(t);
      timers.current.clear();
    };
  }, []);

  return (
    <Ctx.Provider value={{ toast, dismiss }}>
      {children}
      <ToastViewport items={items} dismiss={dismiss} />
    </Ctx.Provider>
  );
}

function ToastViewport({
  items,
  dismiss,
}: {
  items: ToastRow[];
  dismiss: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-end gap-2 p-3 sm:items-end sm:p-5"
      aria-live="polite"
      aria-atomic="false"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastRow; onDismiss: () => void }) {
  const accent =
    item.tone === 'accent'
      ? 'var(--color-clove)'
      : item.tone === 'success'
        ? 'var(--color-leaf)'
        : item.tone === 'warning'
          ? '#f5b942'
          : 'var(--color-cream-3)';
  return (
    <div
      className="toast-card panel-elev pointer-events-auto w-full max-w-sm overflow-hidden text-sm"
      role="status"
      style={{ boxShadow: '0 12px 32px -8px rgb(0 0 0 / 0.55)' }}
    >
      <div className="flex">
        <div
          aria-hidden
          className="w-1 flex-shrink-0"
          style={{ background: accent }}
        />
        <div className="flex-1 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-cream">{item.title}</div>
              {item.body && <div className="mt-1 text-xs text-cream-3">{item.body}</div>}
            </div>
            <button
              type="button"
              className="btn btn-ghost -mr-1.5 -my-1.5 p-1.5"
              aria-label="Dismiss"
              onClick={onDismiss}
            >
              <IconClose size={14} />
            </button>
          </div>
          {item.actions && item.actions.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {item.actions.map((a, i) => (
                <button
                  key={i}
                  type="button"
                  className={
                    a.variant === 'primary'
                      ? 'btn btn-primary text-xs'
                      : 'btn btn-ghost text-xs'
                  }
                  onClick={async () => {
                    const r = await a.onClick();
                    if (!r?.keepOpen) onDismiss();
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
